import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { apiUrl } from '../lib/api';
import { getPixConfig, savePixConfig, type PixConfig } from '../lib/financeiroStorage';
import { resolveExcursaoStatus } from '../../../shared/lib/excursaoStorage';

type StatusReserva = 'em formação' | 'confirmada' | 'cancelada' | 'concluída';
type FormaPagamento = 'cartão' | 'pix' | 'boleto' | '—';

interface TransacaoResumo {
  id: string;
  tipo: 'recebimento' | 'pagamento_guia' | 'provisao' | 'cancelamento' | 'devolucao' | 'taxa';
  descricao: string;
  valor: number;
  data: string;
  status: string;
  formaPagamento?: FormaPagamento;
  reservaId?: string;
}

interface ExcursaoApi {
  id: string;
  date: string;
  status: string;
  city: string;
  maxPessoas: number;
  quorumMinimo: number;
  valorPorPessoa: number;
  inscritos: number;
  horarioSaida: string;
  guiaId?: string;
  title?: string;
  experiences?: number[];
}

const formatCurrency = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function FinanceiroPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'pix' | 'relatorios'>('dashboard');
  const [pixForm, setPixForm] = useState<PixConfig>(getPixConfig());
  const [pixSaving, setPixSaving] = useState(false);
  const [pixSaved, setPixSaved] = useState(false);

  const [reservas, setReservas] = useState<TransacaoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    recebidoTotal: 0,
    aReceberGuia: 0,
    provisoes: 0,
    cancelamentos: 0,
    devolucoes: 0,
    taxasPlataforma: 0,
    saldoPeriodo: 0,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(apiUrl('/api/excursoes'));
        const list: ExcursaoApi[] = res.ok ? await res.json() : [];
        if (cancelled) return;

        const taxaPlataforma = 0.15; // 15% exemplo
        const percentualGuia = 0.70; // 70% para guia

        const transacoes: TransacaoResumo[] = [];
        let recebidoTotal = 0;
        let aReceberGuia = 0;
        let provisoes = 0;
        let cancelamentos = 0;
        let taxas = 0;

        for (const e of list || []) {
          const valorPorPessoa = Number(e.valorPorPessoa) || 0;
          const inscritos = Number(e.inscritos) || 0;
          const arrecadado = valorPorPessoa * inscritos;
          const valorGuia = Math.round(arrecadado * percentualGuia);
          const taxa = Math.round(arrecadado * taxaPlataforma);
          const dataStr = e.date || '';
          const d = dataStr.split('-');
          const dataFormatada = d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : dataStr;

          if (e.status === 'cancelada') {
            cancelamentos += arrecadado;
            transacoes.push({
              id: `${e.id}-cancel`,
              tipo: 'cancelamento',
              descricao: `${e.title || e.city || 'Excursão'} (cancelada)`,
              valor: -arrecadado,
              data: dataFormatada,
              status: 'Cancelada',
              formaPagamento: '—',
              reservaId: e.id,
            });
          } else {
            recebidoTotal += arrecadado;
            aReceberGuia += valorGuia;
            provisoes += valorGuia;
            taxas += taxa;
            transacoes.push({
              id: e.id,
              tipo: 'recebimento',
              descricao: e.title || e.city || 'Excursão',
              valor: arrecadado,
              data: dataFormatada,
              status: (() => {
                const st = resolveExcursaoStatus({
                  status:
                    e.status === 'cancelada' || e.status === 'cancelado' ? 'cancelada' : 'em formação',
                  date: e.date || '',
                  quorumMinimo: Number(e.quorumMinimo) || 0,
                  inscritos: Number(e.inscritos) || 0,
                });
                if (st === 'cancelada') return 'Cancelada';
                if (st === 'concluída') return 'Concluída';
                if (st === 'confirmada') return 'Confirmada';
                return 'Em formação';
              })(),
              formaPagamento: 'pix' as FormaPagamento,
              reservaId: e.id,
            });
            transacoes.push({
              id: `${e.id}-guia`,
              tipo: 'pagamento_guia',
              descricao: `Valor guia - ${e.title || e.city}`,
              valor: -valorGuia,
              data: dataFormatada,
              status: 'Provisão (18h)',
              formaPagamento: 'pix',
              reservaId: e.id,
            });
            transacoes.push({
              id: `${e.id}-taxa`,
              tipo: 'taxa',
              descricao: `Taxa plataforma - ${e.title || e.city}`,
              valor: -taxa,
              data: dataFormatada,
              status: 'Descontado',
              reservaId: e.id,
            });
          }
        }

        setReservas(transacoes.sort((a, b) => (a.data > b.data ? -1 : 1)));
        setKpis({
          recebidoTotal,
          aReceberGuia: provisoes,
          provisoes,
          cancelamentos,
          devolucoes: 0,
          taxasPlataforma: taxas,
          saldoPeriodo: recebidoTotal - provisoes - taxas - cancelamentos,
        });
      } catch {
        if (!cancelled) setReservas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSavePix = () => {
    setPixSaving(true);
    setPixSaved(false);
    try {
      savePixConfig(pixForm);
      setPixForm(getPixConfig());
      setPixSaved(true);
      setTimeout(() => setPixSaved(false), 3000);
    } finally {
      setPixSaving(false);
    }
  };

  const tabs = [
    { id: 'dashboard' as const, label: 'Dashboard Financeiro', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'pix' as const, label: 'Configuração PIX', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'relatorios' as const, label: 'Relatórios', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ];

  return (
    <AdminLayout>
      <div className="bg-white min-h-screen">
        <div className="sticky top-0 z-20 px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">Home &gt; Financeiro</nav>
          <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-colors ${
                  activeTab === t.id
                    ? 'bg-white border border-b-0 border-gray-200 text-orange-600 -mb-px'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
                </svg>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-6">
          {activeTab === 'dashboard' && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
                <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-4 text-white shadow-md">
                  <p className="text-sm opacity-90">Recebimentos</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(kpis.recebidoTotal)}</p>
                  <p className="text-xs opacity-80 mt-1">Valor arrecadado no período</p>
                </div>
                <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-amber-800">Provisões guias</p>
                  <p className="text-2xl font-bold text-amber-900 mt-1">{formatCurrency(kpis.provisoes)}</p>
                  <p className="text-xs text-amber-700 mt-1">Pagamento às 18h (dia do passeio)</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-gray-600">A pagar guias</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(kpis.aReceberGuia)}</p>
                </div>
                <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-red-700">Cancelamentos</p>
                  <p className="text-2xl font-bold text-red-900 mt-1">{formatCurrency(kpis.cancelamentos)}</p>
                </div>
                <div className="bg-white rounded-xl border border-orange-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-orange-700">Devoluções</p>
                  <p className="text-2xl font-bold text-orange-900 mt-1">{formatCurrency(kpis.devolucoes)}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-gray-600">Taxas plataforma</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(kpis.taxasPlataforma)}</p>
                </div>
                <div className="bg-white rounded-xl border-2 border-blue-200 p-4 shadow-sm">
                  <p className="text-sm font-medium text-blue-700">Saldo período</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">{formatCurrency(kpis.saldoPeriodo)}</p>
                </div>
              </div>

              {/* Tabela transações */}
              <section>
                <h2 className="text-lg font-bold text-gray-800 mb-3">Movimentação financeira</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Recebimentos, pagamentos de guias, provisões (liquidado às 18h do dia do passeio), cancelamentos e devoluções.
                </p>
                {loading ? (
                  <div className="text-gray-500 py-8 text-center">Carregando...</div>
                ) : reservas.length === 0 ? (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center text-gray-500">
                    Nenhuma movimentação no momento.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-orange-500 text-white">
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Data</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Pagamento</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservas.map((r, idx) => (
                          <tr key={r.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                r.tipo === 'recebimento' ? 'bg-green-100 text-green-800' :
                                r.tipo === 'pagamento_guia' || r.tipo === 'provisao' ? 'bg-amber-100 text-amber-800' :
                                r.tipo === 'cancelamento' || r.tipo === 'devolucao' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {r.tipo === 'recebimento' && 'Recebimento'}
                                {r.tipo === 'pagamento_guia' && 'Pagamento guia'}
                                {r.tipo === 'provisao' && 'Provisão'}
                                {r.tipo === 'cancelamento' && 'Cancelamento'}
                                {r.tipo === 'devolucao' && 'Devolução'}
                                {r.tipo === 'taxa' && 'Taxa'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{r.descricao}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{r.data}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{r.formaPagamento ?? '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{r.status}</td>
                            <td className={`px-4 py-3 text-sm font-medium text-right ${
                              r.valor >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {r.valor >= 0 ? '+' : ''}{formatCurrency(r.valor)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === 'pix' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Configuração PIX para recebimento</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Chave PIX utilizada nas cobranças aos clientes. Será exibida no checkout (QR Code e código copia e cola).
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chave PIX (CNPJ, e-mail, telefone ou chave aleatória)</label>
                    <input
                      type="text"
                      value={pixForm.chavePix}
                      onChange={(e) => setPixForm((f) => ({ ...f, chavePix: e.target.value }))}
                      placeholder="24354289000105"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex.: CNPJ (apenas números), e-mail ou telefone</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do titular</label>
                    <input
                      type="text"
                      value={pixForm.nomeTitular}
                      onChange={(e) => setPixForm((f) => ({ ...f, nomeTitular: e.target.value }))}
                      placeholder="NAVI EXPERIENCE LTDA"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={pixForm.cidade}
                      onChange={(e) => setPixForm((f) => ({ ...f, cidade: e.target.value.toUpperCase() }))}
                      placeholder="SAO PAULO"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="pixAtivo"
                      checked={pixForm.ativo}
                      onChange={(e) => setPixForm((f) => ({ ...f, ativo: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <label htmlFor="pixAtivo" className="text-sm text-gray-700">PIX ativo para recebimentos</label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleSavePix}
                      disabled={pixSaving}
                      className="px-5 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-70 transition-colors"
                    >
                      {pixSaving ? 'Salvando...' : pixSaved ? 'Salvo!' : 'Salvar configuração'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Orientação sobre pagamento automático */}
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="font-bold text-amber-900 mb-2">Pagamento automático aos guias</h3>
                <p className="text-sm text-amber-800 mb-3">
                  Para que o guia receba automaticamente após a execução do passeio (às 18h), é necessário integrar a plataforma com um gateway de pagamento que suporte <strong>split de PIX</strong>.
                </p>
                <p className="text-sm text-amber-800 mb-3 font-medium">Bancos e soluções recomendadas:</p>
                <ul className="list-disc list-inside text-sm text-amber-800 space-y-1 mb-3">
                  <li><strong>Mercado Pago</strong> – Checkout Pro ou Transparente com split; aceita PIX e cartão</li>
                  <li><strong>Asaas</strong> – API com split de PIX, boleto e cartão; divisão automática entre contas</li>
                  <li><strong>OpenPix / Woovi</strong> – Especializados em PIX; suporte a split para subcontas</li>
                  <li><strong>Banco do Brasil</strong> – API PIX empresarial; requer conta jurídica e integração</li>
                  <li><strong>SICOOB</strong> – API PIX disponível; consultar condições comerciais</li>
                </ul>
                <p className="text-sm text-amber-800">
                  Recomendamos <strong>Mercado Pago</strong> ou <strong>Asaas</strong> pela facilidade de integração e documentação. Configure as chaves PIX dos guias no cadastro e utilize o split no momento da cobrança.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'relatorios' && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-gray-800">Relatórios financeiros</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <h3 className="font-semibold text-gray-900 mb-2">Pagamentos aos guias por período</h3>
                  <p className="text-sm text-gray-500">Exporte valores pagos aos guias por data de execução.</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <h3 className="font-semibold text-gray-900 mb-2">Recebimentos por forma de pagamento</h3>
                  <p className="text-sm text-gray-500">PIX, cartão e boleto – total e por período.</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <h3 className="font-semibold text-gray-900 mb-2">Cancelamentos e devoluções</h3>
                  <p className="text-sm text-gray-500">Resumo de cancelamentos e valores devolvidos.</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <h3 className="font-semibold text-gray-900 mb-2">Provisões pendentes</h3>
                  <p className="text-sm text-gray-500">Valores a pagar aos guias no próximo lote (18h).</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">Exportação em CSV/Excel será implementada na integração com o gateway de pagamento.</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
